<template>
    <main>
        <integration-connector
            logo-classes="bg-primary text-white"
            icon="contract"
            translation-key="multi-posting"
            :active="false"
            :show-tag="hasRequested || hasMultiPostingSubscription || hasBoughtMultiPosting"
            :tag-display="tagDisplay"
            :tag-description="`${ $t(`general.${currentCompany.multiposting_status}`) }`"
            :chosen="prelaunched"
            :connectLabel="hasMultiPostingSubscription ? 'multi-posting.integrations.go_to_module' : 'multi-posting.integrations.request_qoute'"
            :show-extra-button="!hasMultiPostingSubscription"
            :extra-button-arrow-direction="collapseMultiPosting ? 'up' : 'down'"
            :extraButtonLabel="$t('general.discover')"
            :disabled="disableButton"
            :collapse="hasMultiPostingSubscription || collapseMultiPosting"
            :collapse-classes="hasMultiPostingSubscription ? 'mb-24' : null"
            @connect="hasMultiPostingSubscription ? goToModule() : requestQuote()"
            @extraButton="collapseMultiPosting = !collapseMultiPosting"
        >
            <template #collapse>
                <div v-if="multiPostingSubscription || hasRequested" class="flex justify-between text-body-sm">
                    <p class="w-2/3">{{ currentCompany.multiposting_status === 'active' ? $t('multi-posting.integration.cancel.description') : $t('multi-posting.is_requested') }}</p>
                    <p
                        v-if="multiPostingSubscription"
                        class="first-letter:uppercase"
                    >
                        {{ `${ $t('multi-posting.integration.renewal_date') } ` }}
                        <span>
                            {{ `${ cancelDate }` }}
                        </span>
                    </p>
                </div>
                <MultiPostingCollapse
                    v-else
                    @request="requestQuote"
                    @contact="requestContact"
                    :requestingQuote="requestingQuote"
                    :requestingContact="requestingContact"
                />
            </template>
        </integration-connector>
    </main>
</template>

<script>
import IntegrationConnector from './IntegrationConnector.vue';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import EditResource from '../ui/EditResource.vue';
import Input from '../forms/Input.vue';
import Toggle from '../forms/Toggle.vue';
import Company from '../../models/Company';
import {normalizeJson} from '../../../shared/helpers';
import SvgIcon from '../ui/SvgIcon.vue';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import Loading from '../ui/Loading.vue';
import PopupModalConfirm from '../ui/PopupModalConfirm';
import * as multiPosting from '../../services/MultiPostingService';
import PaidSubscription from "../../models/PaidSubscription";
import SimpleButton from "../ui/SimpleButton";
import moment from 'moment';
import * as invoices from '../../services/InvoiceService';
import * as paidSubscriptions from '../../services/PaidSubscriptionService';
import Invoice from "../../models/Invoice";
import InvoiceConfirmationDetails from "../InvoiceConfirmationDetails";
import RegionLabel from "../../mixins/regionLabel";
import { getSymbol } from '../../etc/currency';
import MultiPostingCollapse from "../multiposting/MultiPostingCollapse.vue";

export default {
    name: 'MultiPostingConnector',
    components: {
        MultiPostingCollapse,
        InvoiceConfirmationDetails,
        SimpleButton,
        'integration-connector': IntegrationConnector,
        'edit-resource': EditResource,
        'simple-input': Input,
        'svg-icon': SvgIcon,
        'toggle': Toggle,
        'loading': Loading,
        PopupModalConfirm,
    },
    data: function () {
        return {
            configuring: false,
            showRequestSubscription: false,
            requestingQuote: false,
            requestingContact: false,
            currency: {},
            collapseMultiPosting: false,
        };
    },
    mixins: [
        RegionLabel,
    ],
    props: {
        locations: {
            type: Array,
            required: true,
        },
        prelaunched: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState(useMainStore, ['selectedLocale', 'currentCompany']),
        hasRequested() {
            return this.currentCompany.multiposting_status === 'active' || this.currentCompany.multiposting_status === 'pending';
        },
        hasMultiPostingSubscription() {
            return !!this.multiPostingSubscription;
        },
        multiPostingSubscription() {
            // Here we check if the company has an active multi-posting subscription
            return useRepo(PaidSubscription)
                .where('type', 'multi-posting')
                .where('company_id', this.currentCompany.id)
                .where('active', true)
                .first();
        },
        hasBoughtMultiPosting() {
            // Here we check if the company ever bought a multi-posting subscription
            // The paid subscription can be expired or active
            return useRepo(Invoice)
                .where('type', 'subscription')
                .where('state', ['BOOKED', 'PENDING', 'EXPIRED'])
                .where(invoice => invoice.metadata.type === "multi-posting")
                .first() && this.currentCompany.multiposting_status === 'active';
        },
        disableButton() {
            return this.performingRequest || (this.hasRequested && this.currentCompany.multiposting_status !== 'active');
        },
        cancelDate() {
            if (!this.multiPostingSubscription && this.hasBoughtMultiPosting) {
                return this.$t('general.processing');
            } else if (!this.multiPostingSubscription) return;

            let date = moment(this.multiPostingSubscription.expires_at).locale(this.selectedLocale).format('L');

            if (this.selectedLocale === 'nl') {
                date = date.replace(/-/g, '/');
            }

            return date;
        },
        tagDisplay() {
            if (this.currentCompany.multiposting_status === 'active') {
                return 'success';
            } else if (this.currentCompany.multiposting_status === 'pending') {
                return 'warning';
            }
            return 'danger';
        },
        performingRequest() {
            return this.requestingQuote || this.requestingContact;
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        requestQuote() {
            this.requestingQuote = true;
            multiPosting.requestQuote()
                .then(result => {
                    return useRepo(Company).save(normalizeJson(result));
                })
                .then(() => {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('messages.multi_posting.quote.success'),
                    });
                })
                .finally(() => {
                    this.requestingQuote = false;
                });
        },
        requestContact() {
            this.requestingContact = true;

            multiPosting.requestContact()
                .then(result => {
                    return useRepo(Company).save(normalizeJson(result));
                })
                .then(() => {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.$t('messages.multi_posting.contact.success'),
                    });
                })
                .finally(() => {
                    this.requestingContact = false;
                });
        },
        goToModule() {
            this.$router.push({name: 'multi-posting', params: {tab: 'contracts'}});
        },
        fetchInvoices() {
            return invoices.get(this.currentCompany.id, true).then(result => {
                useRepo(Invoice).save(normalizeJson(result.data))
            });
        },
        fetchPaidSubscriptions() {
            return paidSubscriptions.get(this.currentCompany.id).then(result => {
                useRepo(PaidSubscription).save(normalizeJson(result))
            });
        }
    },
    watch: {
        prelaunched(newVal) {
            if (newVal) {
                setTimeout(this.configure, 500);
            }
        },
    },
    mounted() {
        if (!this.hasMultiPostingSubscription) {
            // If multi-posting isn't enabled let's check if we have an open invoice already for multi-posting
            this.fetchInvoices();
        }
        this.currency = getSymbol();
    },
};
</script>

<style scoped>
.slide-fade-enter-active {
    transition: all .3s ease;
}

.slide-fade-leave-active {
    transition: all .3s ease;
}

.slide-fade-enter, .slide-fade-leave-to {
    opacity: 0;
}

.slide-fade-enter {
    transform: translateY(-10px);
}

.slide-fade-leave-to {
    transform: translateY(10px);
}

</style>

